// Datakobling for konsern/eierskap. Henter workerens flatede Brreg-svar
// (roller + UBO) for ett selskap og bygger opp konsern-modellen etter hvert som
// brukeren navigerer mellom selskapene (akkumulerende N-selskaps-graf).
//
// Real/mock-veksling følger samme mønster som features/admin/api/adminApi.ts.
// Egen, frittstående fil — rører ikke api-agentens hooks.ts/types.ts.
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/lib/apiClient";
import { env } from "@/lib/env";
import {
  konsernNoder,
  leggTilSelskap,
  tomKonsern,
  uboFor,
  type KonsernKilde,
  type KonsernModell,
} from "./konsern";
import { mockKonsernKilder } from "../mocks/konsernMock";

export interface KonsernApi {
  /** Henter Brreg-data (enhet/roller/ubo) for ett selskap. */
  getBrreg(orgnr: string): Promise<KonsernKilde>;
}

const realKonsernApi: KonsernApi = {
  async getBrreg(orgnr) {
    // Workeren returnerer { ok, enhet, roller, regnskap, ubo, uboAvailable }.
    // Vi leser kun KonsernKilde-undersettet (resten ignoreres her).
    return apiClient.get<{ ok: true } & KonsernKilde>(`/selskapsinfo/${orgnr}/brreg`);
  },
};

const mockKonsernApi: KonsernApi = {
  async getBrreg(orgnr) {
    return (
      mockKonsernKilder[orgnr.replace(/\s/g, "")] ?? {
        enhet: null,
        roller: [],
        ubo: [],
        uboAvailable: false,
      }
    );
  },
};

export const konsernApi: KonsernApi = env.useMocks ? mockKonsernApi : realKonsernApi;

/**
 * Akkumulerende konsern-modell rundt et aktivt org.nr. Henter Brreg for det
 * aktive selskapet og fletter det inn i modellen; mor/datter for aktivt selskap
 * utledes av de innsamlede kontroll-kantene. Deler query-cache med `useBrreg`
 * (samme nøkkel) slik at Brreg-kallet ikke gjøres dobbelt.
 */
export function useKonsern(aktivOrg: string | null) {
  const [model, setModel] = useState<KonsernModell>(tomKonsern);

  const query = useQuery({
    queryKey: ["selskapsinfo", "brreg", aktivOrg],
    enabled: !!aktivOrg,
    staleTime: 60 * 60 * 1000, // Brreg endrer seg sjelden
    queryFn: () => konsernApi.getBrreg(aktivOrg as string),
  });

  useEffect(() => {
    if (aktivOrg && query.data) {
      setModel((m) => leggTilSelskap(m, aktivOrg, query.data));
    }
  }, [aktivOrg, query.data]);

  return {
    model,
    noder: aktivOrg ? konsernNoder(model, aktivOrg) : [],
    ubo: aktivOrg ? uboFor(model, aktivOrg) : { eiere: [], tilgjengelig: false },
    isLoading: query.isLoading,
    error: query.error,
  };
}
